import type { ReactNode } from "react";

/* THE ONE ICON SET (W81 amendment (f), the pro web step 1) — Lucide, drawn inline at 18 px with a 1.6 stroke.
 *
 * ⚠️ ONE MODULE, ONE SOURCE. Every nav item, tile and control that carries an icon asks for it HERE by name; a second SVG
 * source is how two strokes and two corner styles end up on one screen (gate `w81-1-the-tokens-and-the-shell` counts the
 * shell's icons through this file). The paths are Lucide's own (https://lucide.dev), copied rather than installed:
 * six shapes do not earn a dependency, and a package would put a request's worth of code in the bundle for them.
 *
 * Lucide is ISC-licensed: "Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2022 as part of Feather (MIT).
 * All other copyright (c) for Lucide are held by Lucide Contributors 2022. Permission to use, copy, modify, and/or
 * distribute this software for any purpose with or without fee is hereby granted, provided that the above copyright notice
 * and this permission notice appear in all copies."
 *
 * ⚠️ EVERY ICON IS `aria-hidden`. The control beside it carries the name — an icon that announces itself makes a screen
 * reader say "Clients Clients".
 */
export type IconName = "dashboard" | "clients" | "library" | "inbox" | "search" | "sun" | "moon"
  // PRO WEB STEP 7 (Fable's ruling, 2026-09-26): every hand-drawn icon on every page moved HERE, as Lucide.
  | "check" | "x" | "play" | "pause" | "trash" | "list" | "grid" | "chevron-down" | "chevron-left" | "star" | "mic"
  | "info" | "ellipsis" | "grip" | "filter" | "link" | "video" | "arrow-up-right" | "arrow-down-right" | "timer"
  | "medal" | "smile" | "reply" | "trophy" | "dumbbell" | "chart" | "refresh" | "sigma" | "clock" | "hourglass"
  // W85 (2026-09-27): the builder's Undo · Redo — the phone's `arrow.uturn.backward` / `.forward` are their twins.
  | "undo" | "redo"
  // W93 (2026-09-28): the review's client note on a set — a speech bubble, opened on hover or a click.
  | "message"
  // W96's pass (2026-09-30): the builder's Phone preview when its work column is too narrow for the words.
  | "phone"
  // W115b (2026-10-01, Fable on his words): the client Week card's Preview — an EYE means "see it"; a magnifier means search.
  | "eye"
  // W119 (2026-10-01, his words): the hover strips' Edit and Copy (the trash is `trash`).
  | "pencil" | "copy"
  // 2026-10-16 (his Instagram still): the emoji picker's group tabs — line icons, never emoji in the chrome (W81 ruler 9).
  | "paw" | "utensils" | "ball" | "car" | "bulb" | "shapes" | "flag"
  // W137 (2026-10-02, William's third look): the extended Week's per-day back-to-top.
  | "arrow-up"
  // W137 cut 2 (his words: "a drag icon like this so the 4 arrow thing"): the day drag's handle.
  | "move"
  // W140 (his 2026-10-02 word: "a lock/unlock button"): the sidebar's lock — closed = the menu stays, open = it hides.
  | "lock" | "lock-open"
  // 2026-10-07 (his word ~12:20, "add a photo icon to upload photos"): the review's Photo.
  | "image"
  // 2026-10-07 Part D (Fable): the overall review's `Live` — the screen it records.
  | "monitor"
  // job 3 step 5 page 2 (2026-10-09, his 10-07 composer): the review composer's + (the phone's `plus`).
  | "plus"
  // Calls session 2 (2026-10-12): the call screen's muted mic, stopped camera and End (the phone's `mic.slash`,
  // `video.slash`, `phone.down`). `phone-down` is Lucide's `phone` turned 135° — the handset laid down, the system's hang-up.
  | "mic-off" | "video-off" | "phone-down";

/* ⚠️ TWO SHAPES ARE FILLED, AND ONLY TWO: `play` and `pause` — a solid media triangle and solid bars are the shapes every
   player on every platform uses, and an outlined triangle inside a round play button reads as a different control. (His
   gold STAR is filled by its own CSS `fill` when chosen — the icon itself stays Lucide's outline — so it is not a variant.) */
const FILLED: ReadonlySet<IconName> = new Set<IconName>(["play", "pause"]);

/** `size` in px (18 = the nav's); `stroke` only where a mark must hold its weight at a small size (the done tick). */
export function Icon({ name, className, size = 18, stroke = 1.6 }: { name: IconName; className?: string; size?: number; stroke?: number }) {
  const filled = FILLED.has(name);
  return (
    <svg className={className === undefined ? "ht-icon" : `ht-icon ${className}`} width={size} height={size}
         viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill={filled ? "currentColor" : "none"} stroke="currentColor"
         strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
      {PATHS[name]}
    </svg>
  );
}

const PATHS: Record<IconName, ReactNode> = {
  // lucide: mic-off (v0.460.0)
  "mic-off": <><line x1="2" x2="22" y1="2" y2="22" /><path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2" /><path d="M5 10v2a7 7 0 0 0 12 5" /><path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" /><path d="M9 9v3a3 3 0 0 0 5.12 2.12" /><line x1="12" x2="12" y1="19" y2="22" /></>,
  // lucide: video-off (v0.460.0)
  "video-off": <><path d="M10.66 6H14a2 2 0 0 1 2 2v2.5l5.248-3.062A.5.5 0 0 1 22 7.87v8.196" /><path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2" /><path d="m2 2 20 20" /></>,
  // lucide: phone (v0.460.0), turned 135°
  "phone-down": <g transform="rotate(135 12 12)"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></g>,
  // lucide: undo-2
  undo: (
    <>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11" />
    </>
  ),
  // lucide: redo-2
  redo: (
    <>
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13" />
    </>
  ),
  // lucide: layout-dashboard
  dashboard: (
    <>
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="12" rx="1" />
      <rect width="7" height="5" x="3" y="16" rx="1" />
    </>
  ),
  // lucide: users
  clients: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  // lucide: library
  library: (
    <>
      <path d="m16 6 4 14" />
      <path d="M12 6v14" />
      <path d="M8 8v12" />
      <path d="M4 4v16" />
    </>
  ),
  // lucide: inbox
  inbox: (
    <>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),
  // lucide: search
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  // lucide: sun
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </>
  ),
  // lucide: moon
  moon: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />,
  // lucide: check
  check: <path d="M20 6 9 17l-5-5" />,
  // lucide: x
  x: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
  // lucide: play (filled — see FILLED)
  play: <path d="M6 3 20 12 6 21Z" />,
  // lucide: pause (filled — see FILLED)
  pause: <><rect x="14" y="4" width="4" height="16" rx="1" /><rect x="6" y="4" width="4" height="16" rx="1" /></>,
  // lucide: trash-2
  trash: <><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><path d="M10 11v6" /><path d="M14 11v6" /></>,
  // lucide: list
  list: <><path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" /><path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" /></>,
  // lucide: layout-grid
  grid: <><rect width="7" height="7" x="3" y="3" rx="1" /><rect width="7" height="7" x="14" y="3" rx="1" /><rect width="7" height="7" x="14" y="14" rx="1" /><rect width="7" height="7" x="3" y="14" rx="1" /></>,
  // lucide: chevron-down
  "chevron-down": <path d="m6 9 6 6 6-6" />,
  // lucide: chevron-left
  "chevron-left": <path d="m15 18-6-6 6-6" />,
  // lucide: star
  star: <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />,
  // lucide: mic
  mic: <><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><path d="M12 19v3" /></>,
  // lucide: info
  info: <><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></>,
  // lucide: ellipsis
  ellipsis: <><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></>,
  // lucide: grip-vertical
  grip: <><circle cx="9" cy="12" r="1" /><circle cx="9" cy="5" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="19" r="1" /></>,
  // lucide: list-filter
  filter: <><path d="M3 6h18" /><path d="M7 12h10" /><path d="M10 18h4" /></>,
  // lucide: link
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  // lucide: video
  // lucide: smartphone
  phone: <><rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" /></>,
  // lucide: pencil
  pencil: <><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" /><path d="m15 5 4 4" /></>,
  // lucide: copy
  // lucide: paw-print · utensils · dribbble · car · lightbulb · shapes · flag (the emoji picker's group tabs)
  paw: <><circle cx="11" cy="4" r="2" /><circle cx="18" cy="8" r="2" /><circle cx="20" cy="16" r="2" /><path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" /></>,
  utensils: <><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" /></>,
  ball: <><circle cx="12" cy="12" r="10" /><path d="M19.13 5.09C15.22 9.14 10 10.44 2.25 10.94" /><path d="M21.75 12.84c-6.62-1.41-12.14 1-16.38 6.32" /><path d="M8.56 2.75c4.37 6 6 9.42 8 17.72" /></>,
  car: <><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" /><circle cx="7" cy="17" r="2" /><path d="M9 17h6" /><circle cx="17" cy="17" r="2" /></>,
  bulb: <><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" /><path d="M9 18h6" /><path d="M10 22h4" /></>,
  shapes: <><path d="M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z" /><rect x="3" y="14" width="7" height="7" rx="1" /><circle cx="17.5" cy="17.5" r="3.5" /></>,
  flag: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" x2="4" y1="22" y2="15" /></>,
  copy: <><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></>,
  // lucide: eye
  eye: <><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></>,
  video: <><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" /><rect x="2" y="6" width="14" height="12" rx="2" /></>,
  // lucide: image
  image: <><rect width="18" height="18" x="3" y="3" rx="2" ry="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></>,
  // lucide: plus
  plus: <><path d="M5 12h14" /><path d="M12 5v14" /></>,
  // lucide: monitor
  monitor: <><rect width="20" height="14" x="2" y="3" rx="2" /><line x1="8" x2="16" y1="21" y2="21" /><line x1="12" x2="12" y1="17" y2="21" /></>,
  // lucide: lock
  lock: <><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  // lucide: lock-open
  "lock-open": <><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></>,
  // lucide: move
  move: <><path d="M12 2v20" /><path d="m15 19-3 3-3-3" /><path d="m19 9 3 3-3 3" /><path d="M2 12h20" /><path d="m5 9-3 3 3 3" /><path d="m9 5 3-3 3 3" /></>,
  // lucide: arrow-up
  "arrow-up": <><path d="m5 12 7-7 7 7" /><path d="M12 19V5" /></>,
  // lucide: arrow-up-right
  "arrow-up-right": <><path d="M7 7h10v10" /><path d="M7 17 17 7" /></>,
  // lucide: arrow-down-right
  "arrow-down-right": <><path d="m7 7 10 10" /><path d="M17 7v10H7" /></>,
  // lucide: timer
  timer: <><path d="M10 2h4" /><path d="m12 14 3-3" /><circle cx="12" cy="14" r="8" /></>,
  // lucide: medal
  medal: <><path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15" /><path d="M11 12 5.12 2.2" /><path d="m13 12 5.88-9.8" /><path d="M8 7h8" /><circle cx="12" cy="17" r="5" /><path d="M12 18v-2h-.5" /></>,
  // lucide: smile
  smile: <><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><path d="M9 9h.01" /><path d="M15 9h.01" /></>,
  // lucide: reply
  reply: <><path d="M9 17 4 12l5-5" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></>,
  // lucide: message-circle
  message: <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />,
  // lucide: trophy
  trophy: <><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></>,
  // lucide: dumbbell
  dumbbell: <><path d="m6.5 6.5 11 11" /><path d="m21 21-1-1" /><path d="m3 3 1 1" /><path d="m18 22 4-4" /><path d="m2 6 4-4" /><path d="m3 10 7-7" /><path d="m14 21 7-7" /></>,
  // lucide: chart-column
  chart: <><path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></>,
  // lucide: refresh-cw
  refresh: <><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></>,
  // lucide: sigma
  sigma: <path d="M18 7V4H6l6 8-6 8h12v-3" />,
  // lucide: clock
  clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  // lucide: hourglass
  hourglass: <><path d="M5 22h14" /><path d="M5 2h14" /><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" /><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" /></>,
};
